/**
 * Get Fabulous – MediaPipe Face Mesh Try On
 * Isolated module: does not touch ATC, cart, gallery, or theme globals.
 * Uses classic Face Mesh script tags (Shopify-safe) instead of ESM dynamic import.
 */
(function () {
  'use strict';

  if (window.GetFabTryOn && window.GetFabTryOn.__ready) return;

  var FACE_MESH_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh@0.4.1633559619';
  var FACE_MESH_SCRIPT = FACE_MESH_BASE + '/face_mesh.js';

  // Forehead / temple landmarks for headband placement (Face Mesh indices)
  var LM_FOREHEAD = 10;
  var LM_LEFT = 54;
  var LM_RIGHT = 284;
  var LM_GLABELLA = 151;

  var state = {
    root: null,
    modal: null,
    video: null,
    canvas: null,
    ctx: null,
    statusEl: null,
    stream: null,
    faceMesh: null,
    animFrame: null,
    productImg: null,
    productImgUrl: '',
    lastVideoTime: -1,
    opening: false,
    detectErrors: 0,
    listenersBound: false,
    sending: false,
    latestLandmarks: null
  };

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function setStatus(msg, isError) {
    if (!state.statusEl) return;
    state.statusEl.textContent = msg || '';
    state.statusEl.classList.toggle('is-error', !!isError);
  }

  function normalizeImageUrl(url) {
    if (!url) return '';
    if (url.indexOf('//') === 0) return 'https:' + url;
    return url;
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      url = normalizeImageUrl(url);
      if (!url) {
        reject(new Error('No product image'));
        return;
      }

      function attempt(useCors) {
        var img = new Image();
        if (useCors) img.crossOrigin = 'anonymous';
        img.onload = function () {
          resolve(img);
        };
        img.onerror = function () {
          if (useCors) {
            attempt(false);
          } else {
            reject(new Error('Product image failed to load'));
          }
        };
        img.src = url;
      }

      attempt(true);
    });
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[data-getfab-mp="' + src + '"]');
      if (existing) {
        if (window.FaceMesh) {
          resolve();
          return;
        }
        existing.addEventListener('load', function () {
          resolve();
        });
        existing.addEventListener('error', function () {
          reject(new Error('MEDIAPIPE_LOAD_FAILED'));
        });
        return;
      }

      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.setAttribute('data-getfab-mp', src);
      script.onload = function () {
        resolve();
      };
      script.onerror = function () {
        reject(new Error('MEDIAPIPE_LOAD_FAILED'));
      };
      document.head.appendChild(script);
    });
  }

  function ensureMediaPipe() {
    if (state.faceMesh) {
      return Promise.resolve(state.faceMesh);
    }

    return loadScript(FACE_MESH_SCRIPT)
      .then(function () {
        if (typeof window.FaceMesh !== 'function') {
          throw new Error('MEDIAPIPE_LOAD_FAILED');
        }

        var faceMesh = new window.FaceMesh({
          locateFile: function (file) {
            return FACE_MESH_BASE + '/' + file;
          }
        });

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5
        });

        faceMesh.onResults(function (results) {
          if (results && results.multiFaceLandmarks && results.multiFaceLandmarks.length) {
            state.latestLandmarks = results.multiFaceLandmarks[0];
            state.detectErrors = 0;
            drawHeadband(state.latestLandmarks);
            setStatus('Gesicht erkannt – bewege Dich leicht für die beste Passform.');
          } else {
            state.latestLandmarks = null;
            if (state.ctx && state.canvas) {
              state.ctx.clearRect(0, 0, state.canvas.width, state.canvas.height);
            }
            setStatus('Suche Gesicht… bitte schau in die Kamera.');
          }
        });

        state.faceMesh = faceMesh;
        return faceMesh;
      })
      .catch(function (err) {
        if (window.console && console.warn) {
          console.warn('[GetFab Try On] MediaPipe Face Mesh load failed', err);
        }
        throw new Error('MEDIAPIPE_LOAD_FAILED');
      });
  }

  function stopCamera() {
    if (state.animFrame) {
      cancelAnimationFrame(state.animFrame);
      state.animFrame = null;
    }
    state.sending = false;
    state.latestLandmarks = null;
    if (state.stream) {
      state.stream.getTracks().forEach(function (track) {
        track.stop();
      });
      state.stream = null;
    }
    if (state.video) {
      try {
        state.video.pause();
      } catch (e) {}
      state.video.srcObject = null;
    }
    if (state.ctx && state.canvas) {
      state.ctx.clearRect(0, 0, state.canvas.width, state.canvas.height);
    }
    state.lastVideoTime = -1;
    state.detectErrors = 0;
  }

  function closeModal() {
    stopCamera();
    if (state.modal) {
      state.modal.classList.remove('is-open');
      state.modal.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('getfab-tryon__body-lock');
    setStatus('');
    state.opening = false;
  }

  function drawHeadband(landmarks) {
    var canvas = state.canvas;
    var ctx = state.ctx;
    var img = state.productImg;
    if (!canvas || !ctx || !img || !landmarks || landmarks.length < 300) return;

    var w = canvas.width;
    var h = canvas.height;
    if (!w || !h) return;

    ctx.clearRect(0, 0, w, h);

    var left = landmarks[LM_LEFT];
    var right = landmarks[LM_RIGHT];
    var forehead = landmarks[LM_FOREHEAD];
    var glabella = landmarks[LM_GLABELLA];
    if (!left || !right || !forehead || !glabella) return;

    var x1 = left.x * w;
    var y1 = left.y * h;
    var x2 = right.x * w;
    var y2 = right.y * h;
    var cx = ((left.x + right.x) / 2) * w;
    var cy = forehead.y * h - (glabella.y - forehead.y) * h * 0.35;

    var faceWidth = Math.hypot(x2 - x1, y2 - y1);
    if (!faceWidth || faceWidth < 8) return;

    var bandWidth = faceWidth * 1.45;
    var bandHeight = bandWidth * (img.naturalHeight / Math.max(img.naturalWidth, 1)) * 0.55;
    bandHeight = Math.min(Math.max(bandHeight, faceWidth * 0.22), faceWidth * 0.55);

    var angle = Math.atan2(y2 - y1, x2 - x1);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(angle);
    ctx.globalAlpha = 0.92;
    ctx.drawImage(img, -bandWidth / 2, -bandHeight / 2, bandWidth, bandHeight);
    ctx.restore();
  }

  function renderLoop() {
    if (!state.video || !state.faceMesh || !state.modal || !state.modal.classList.contains('is-open')) {
      return;
    }

    var video = state.video;
    if (video.readyState >= 2 && !state.sending) {
      syncCanvasSize();
      if (video.currentTime !== state.lastVideoTime) {
        state.lastVideoTime = video.currentTime;
        state.sending = true;
        state.faceMesh
          .send({ image: video })
          .catch(function (err) {
            state.detectErrors += 1;
            if (window.console && console.warn) {
              console.warn('[GetFab Try On] detect error', err);
            }
            if (state.detectErrors >= 5) {
              setStatus('Try On kurz unterbrochen. Bitte erneut öffnen.', true);
              stopCamera();
            }
          })
          .then(function () {
            state.sending = false;
          });
      }
    }

    state.animFrame = requestAnimationFrame(renderLoop);
  }

  function syncCanvasSize() {
    if (!state.video || !state.canvas) return;
    var w = state.video.videoWidth || 0;
    var h = state.video.videoHeight || 0;
    if (!w || !h) return;
    if (state.canvas.width !== w || state.canvas.height !== h) {
      state.canvas.width = w;
      state.canvas.height = h;
    }
  }

  function openModal() {
    if (state.opening) return;
    if (!state.root || !state.modal || !state.video) return;

    if (state.modal.classList.contains('is-open') && state.stream) return;

    state.opening = true;
    state.detectErrors = 0;

    state.modal.classList.add('is-open');
    state.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('getfab-tryon__body-lock');
    setStatus('Kamera & Modell werden geladen…');

    var imageUrl = state.root.getAttribute('data-product-image') || '';

    Promise.resolve()
      .then(function () {
        if (state.productImg && state.productImgUrl === imageUrl) {
          return state.productImg;
        }
        return loadImage(imageUrl).then(function (img) {
          state.productImg = img;
          state.productImgUrl = imageUrl;
          return img;
        });
      })
      .then(function () {
        return ensureMediaPipe();
      })
      .then(function () {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('CAMERA_UNSUPPORTED');
        }
        return navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: 'user',
            width: { ideal: 720 },
            height: { ideal: 960 }
          }
        });
      })
      .then(function (stream) {
        if (!state.modal.classList.contains('is-open')) {
          stream.getTracks().forEach(function (t) {
            t.stop();
          });
          throw new Error('MODAL_CLOSED');
        }
        state.stream = stream;
        state.video.srcObject = stream;
        return new Promise(function (resolve, reject) {
          var settled = false;
          var timer = setTimeout(function () {
            if (settled) return;
            settled = true;
            state.video.removeEventListener('loadedmetadata', onReady);
            reject(new Error('CAMERA_TIMEOUT'));
          }, 10000);

          function onReady() {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            state.video.removeEventListener('loadedmetadata', onReady);
            syncCanvasSize();
            var playResult = state.video.play();
            if (playResult && typeof playResult.then === 'function') {
              playResult.then(resolve).catch(reject);
            } else {
              resolve();
            }
          }
          state.video.addEventListener('loadedmetadata', onReady);
          if (state.video.readyState >= 1) onReady();
        });
      })
      .then(function () {
        syncCanvasSize();
        setStatus('Suche Gesicht…');
        renderLoop();
        state.opening = false;
      })
      .catch(function (err) {
        state.opening = false;
        if (err && err.message === 'MODAL_CLOSED') return;

        stopCamera();

        var name = (err && err.name) || '';
        var msg = (err && err.message) || '';

        if (window.console && console.warn) {
          console.warn('[GetFab Try On]', err);
        }

        if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
          setStatus(
            'Kamera-Zugriff wurde verweigert. Bitte erlaube die Kamera in den Browser-Einstellungen und versuche es erneut.',
            true
          );
        } else if (msg === 'CAMERA_UNSUPPORTED') {
          setStatus('Dein Browser unterstützt keine Kamera für Try On.', true);
        } else if (msg === 'CAMERA_TIMEOUT') {
          setStatus('Kamera startet nicht. Bitte schließe und versuche es erneut.', true);
        } else if (msg === 'MEDIAPIPE_LOAD_FAILED') {
          setStatus(
            'AR-Modell konnte nicht geladen werden. Bitte Seite neu laden oder Adblocker kurz deaktivieren.',
            true
          );
        } else if (msg.indexOf('Product image') !== -1) {
          setStatus('Produktbild für Try On fehlt.', true);
        } else {
          setStatus(
            'Try On konnte nicht gestartet werden. Die Produktseite bleibt normal nutzbar.',
            true
          );
        }
      });
  }

  function bindRoot(root) {
    state.root = root;
    state.modal = qs('[data-getfab-tryon-modal]', root);
    state.video = qs('[data-getfab-tryon-video]', root);
    state.canvas = qs('[data-getfab-tryon-canvas]', root);
    state.statusEl = qs('[data-getfab-tryon-status]', root);
    state.ctx = state.canvas ? state.canvas.getContext('2d') : null;

    var openBtn = qs('[data-getfab-tryon-open]', root);
    var closeBtns = root.querySelectorAll('[data-getfab-tryon-close]');

    if (openBtn) {
      openBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      });
    }

    closeBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeModal();
      });
    });

    if (!state.listenersBound) {
      state.listenersBound = true;
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && state.modal && state.modal.classList.contains('is-open')) {
          closeModal();
        }
      });
      window.addEventListener('pagehide', closeModal);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) closeModal();
      });
    }
  }

  function init() {
    var root = document.querySelector('[data-getfab-tryon]');
    if (!root || root.getAttribute('data-bound') === '1') return;
    root.setAttribute('data-bound', '1');
    bindRoot(root);
  }

  window.GetFabTryOn = {
    __ready: true,
    init: init,
    open: openModal,
    close: closeModal
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
